/* Yvezi theme runtime — tabs, sticky add-to-bag, quick-size, hover mega menus,
   collapsible footer, press-quote rotator, shop-the-look outfit, size-guide units.
   Behaviour reconstructed from the measured reference (rAF-ticked, passive listeners). */
(() => {
  const desktop = window.matchMedia('(min-width: 1000px)');

  /* ---------- Tabs (product rails) ---------- */
  if (!customElements.get('yv-tabs')) {
    customElements.define(
      'yv-tabs',
      class extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
          if (!this.tabs.length) return;
          this.tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => this.select(i));
            tab.addEventListener('keydown', (e) => {
              const map = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
              if (!(e.key in map)) return;
              e.preventDefault();
              const n =
                map[e.key] === 'first'
                  ? 0
                  : map[e.key] === 'last'
                  ? this.tabs.length - 1
                  : (i + map[e.key] + this.tabs.length) % this.tabs.length;
              this.select(n, true);
            });
          });
        }
        select(index, focus) {
          this.tabs.forEach((tab, i) => {
            const on = i === index;
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
            tab.tabIndex = on ? 0 : -1;
            const panel = document.getElementById(tab.getAttribute('aria-controls'));
            if (panel) panel.hidden = !on;
          });
          this.querySelectorAll('[data-yv-tab-link]').forEach((a) => {
            a.hidden = Number(a.dataset.yvTabLink) !== index;
          });
          if (focus) this.tabs[index].focus();
        }
      }
    );
  }

  /* ---------- Sticky add-to-bag (z-index 80, transform .22s) ---------- */
  if (!customElements.get('yv-sticky-atb')) {
    customElements.define(
      'yv-sticky-atb',
      class extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.button = this.querySelector('button');
          this.detail = this.querySelector('[data-yv-sticky-detail]');
          this.label = this.querySelector('[data-yv-sticky-label]');
          if (!this.button) return;
          this.onClick = () => {
            const main = this.mainButton();
            if (!main || main.disabled) return;
            main.click();
          };
          this.button.addEventListener('click', this.onClick);
          this.bind();
          this.readSelected();
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
              if (!e || !e.data || e.data.sectionId !== this.sectionId) return;
              this.setDetail(e.data.variant);
              requestAnimationFrame(() => this.bind());
            });
          }
        }
        disconnectedCallback() {
          if (this.io) this.io.disconnect();
          if (this.mo) this.mo.disconnect();
          if (this.onScrollFallback) {
            window.removeEventListener('scroll', this.onScrollFallback);
            window.removeEventListener('resize', this.onScrollFallback);
          }
          if (this.unsub) this.unsub();
        }
        mainButton() {
          return document.querySelector(`#product-form-${this.sectionId} [type="submit"]`);
        }
        bind() {
          const main = this.mainButton();
          if (!main || main === this.bound) {
            this.sync();
            return;
          }
          this.bound = main;
          if (this.io) this.io.disconnect();
          if (this.mo) this.mo.disconnect();
          const apply = (past) => {
            this.classList.toggle('is-visible', past);
            this.setAttribute('aria-hidden', past ? 'false' : 'true');
            this.button.tabIndex = past ? 0 : -1;
          };
          this.io = new IntersectionObserver(([entry]) => {
            apply(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
          });
          this.io.observe(main);
          this.onScrollFallback = () => {
            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
              this._raf = null;
              apply(main.getBoundingClientRect().bottom < 0);
            });
          };
          window.addEventListener('scroll', this.onScrollFallback, { passive: true });
          window.addEventListener('resize', this.onScrollFallback);
          this.onScrollFallback();
          this.mo = new MutationObserver(() => this.sync());
          this.mo.observe(main, { attributes: true, childList: true, subtree: true, characterData: true });
          this.sync();
        }
        sync() {
          const main = this.bound;
          if (!main) return;
          this.button.disabled = main.disabled;
          const text = main.querySelector('span');
          if (text && this.label) this.label.textContent = text.textContent.trim();
        }
        readSelected() {
          const json = document.querySelector(`#variant-selects-${this.sectionId} [data-selected-variant]`);
          if (!json) return;
          try {
            this.setDetail(JSON.parse(json.textContent));
          } catch (e) {}
        }
        setDetail(variant) {
          if (!this.detail) return;
          this.detail.textContent = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
        }
      }
    );
  }

  /* ---------- Press quotes rotator (autoplay 6s, tabs) ---------- */
  if (!customElements.get('yv-press')) {
    customElements.define(
      'yv-press',
      class extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
          this.slides = Array.from(this.querySelectorAll('[role="tabpanel"]'));
          if (!this.tabs.length) return;
          this.index = Math.max(0, this.tabs.findIndex((t) => t.classList.contains('is-active')));
          this.tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => {
              this.go(i);
              this.stop();
            });
          });
          this.show(this.index);
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (this.dataset.autoplay === 'true' && !reduced) {
            this.interval = (parseInt(this.dataset.interval, 10) || 6) * 1000;
            this.timer = setInterval(() => this.go(this.index + 1), this.interval);
            this.addEventListener('mouseenter', () => this.stop());
            this.addEventListener('focusin', () => this.stop());
          }
        }
        disconnectedCallback() {
          this.stop();
        }
        stop() {
          if (this.timer) clearInterval(this.timer);
          this.timer = null;
        }
        go(i) {
          this.index = (i + this.tabs.length) % this.tabs.length;
          this.show(this.index);
        }
        show(index) {
          this.tabs.forEach((tab, i) => {
            const on = i === index;
            tab.classList.toggle('is-active', on);
            tab.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        }
      }
    );
  }

  /* ---------- Shop the look: outfit add + scroll arrow ---------- */
  if (!customElements.get('yv-outfit')) {
    customElements.define(
      'yv-outfit',
      class extends HTMLElement {
        connectedCallback() {
          this.rows = Array.from(this.querySelectorAll('[data-stl-row]'));
          this.add = this.querySelector('[data-stl-add]');
          this.error = this.querySelector('[data-stl-error]');
          this.rows.forEach((row) => {
            const select = row.querySelector('[data-stl-variant]');
            if (!select) return;
            select.addEventListener('change', () => this.syncRow(row));
            this.syncRow(row);
          });
          if (this.add) this.add.addEventListener('click', () => this.submit());
          const section = this.closest('.yv-stl');
          if (section) {
            section.querySelectorAll('[data-stl-scroll]').forEach((btn) =>
              btn.addEventListener('click', () => this.scrollIntoView({ behavior: 'smooth', block: 'start' }))
            );
          }
        }
        syncRow(row) {
          const select = row.querySelector('[data-stl-variant]');
          const opt = select.options[select.selectedIndex];
          const price = row.querySelector('[data-stl-sale]');
          const compare = row.querySelector('[data-stl-compare]');
          if (price && opt.dataset.price) price.textContent = opt.dataset.price;
          if (compare) {
            if (opt.dataset.compare) {
              compare.textContent = opt.dataset.compare;
              compare.hidden = false;
              price.classList.add('is-sale');
            } else {
              compare.hidden = true;
              price.classList.remove('is-sale');
            }
          }
          row.classList.toggle('is-soldout', opt.dataset.available === 'false');
        }
        async submit() {
          if (!this.add || this.add.getAttribute('aria-busy') === 'true') return;
          const items = this.rows
            .map((row) => row.querySelector('[data-stl-variant]'))
            .filter((s) => s && s.options[s.selectedIndex].dataset.available !== 'false')
            .map((s) => ({ id: Number(s.value), quantity: 1 }));
          this.showError('');
          if (!items.length) {
            this.showError(this.dataset.errorEmpty || 'These pieces are currently unavailable.');
            return;
          }
          this.add.setAttribute('aria-busy', 'true');
          try {
            const drawer = document.querySelector('cart-drawer');
            const body = { items };
            if (drawer && typeof drawer.getSectionsToRender === 'function') {
              body.sections = drawer.getSectionsToRender().map((s) => s.id);
              body.sections_url = window.location.pathname;
            }
            const res = await fetch(`${window.routes ? routes.cart_add_url : '/cart/add'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add the outfit.');
            if (drawer && typeof drawer.renderContents === 'function') {
              drawer.setActiveElement && drawer.setActiveElement(this.add);
              await drawer.renderContents(data);
            } else {
              window.location.href = window.routes ? routes.cart_url : '/cart';
            }
          } catch (e) {
            this.showError(e.message || 'Could not add the outfit.');
          } finally {
            this.add.removeAttribute('aria-busy');
          }
        }
        showError(msg) {
          if (!this.error) return;
          this.error.textContent = msg;
          this.error.classList.toggle('is-visible', !!msg);
        }
      }
    );
  }

  /* ---------- Quick size (styled-looks / style-with cards) ---------- */
  if (!customElements.get('yv-quick-size')) {
    customElements.define(
      'yv-quick-size',
      class extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input[name="id"]');
          this.submit = this.querySelector('[type="submit"]');
          this.querySelectorAll('[data-variant-id]').forEach((button) => {
            button.addEventListener('click', () => {
              this.querySelectorAll('[data-variant-id]').forEach((b) => {
                b.classList.toggle('is-active', b === button);
                b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
              });
              if (this.input) this.input.value = button.dataset.variantId;
              if (this.submit) {
                this.submit.disabled = false;
                this.submit.removeAttribute('aria-disabled');
              }
            });
          });
        }
      }
    );
  }

  /* ---------- Size guide unit toggle (cm / in) ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-size-unit]');
    if (!button) return;
    const guide = button.closest('[data-yv-size-guide]');
    if (!guide) return;
    const inches = button.dataset.sizeUnit === 'in';
    guide.querySelectorAll('[data-size-unit]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    guide.querySelectorAll('[data-cm], [data-cm-range]').forEach((cell) => {
      const values = (cell.dataset.cmRange || cell.dataset.cm).split(':');
      cell.textContent = inches
        ? values.map((cm) => (Number(cm) / 2.54).toFixed(1)).join('–')
        : values.join('–');
    });
  });

  /* ---------- Hover-open mega menus (90ms intent delay) ---------- */
  const initHoverMenus = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('.section-header header-menu details.mega-menu').forEach((details) => {
      if (details.dataset.yvHover) return;
      details.dataset.yvHover = 'true';
      let timer;
      const li = details.closest('li');
      li.addEventListener('mouseenter', () => {
        if (!desktop.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          document.querySelectorAll('.section-header details.mega-menu[open]').forEach((d) => {
            if (d !== details) d.querySelector('summary').click();
          });
          if (!details.open) details.querySelector('summary').click();
        }, 90);
      });
      li.addEventListener('mouseleave', () => clearTimeout(timer));
    });
    const header = document.querySelector('.section-header');
    if (header && !header.dataset.yvHover) {
      header.dataset.yvHover = 'true';
      header.addEventListener('mouseleave', () => {
        if (!desktop.matches) return;
        header.querySelectorAll('details.mega-menu[open]').forEach((d) => d.querySelector('summary').click());
      });
    }
  };

  /* ---------- Footer collapsible menus (mobile only) ---------- */
  const initFooter = () => {
    document.querySelectorAll('.footer-block--menu').forEach((block, i) => {
      const heading = block.querySelector('.footer-block__heading');
      const list = block.querySelector('.footer-block__details-content');
      if (!heading || !list || block.dataset.yvFooter) return;
      block.dataset.yvFooter = 'true';
      block.classList.add('yv-footer-collapsible');
      list.id = list.id || `YvFooterMenu-${i}`;
      const control = heading.querySelector('.footer-block__toggle') || heading;
      control.setAttribute('aria-controls', list.id);
      const sync = () => {
        const collapsible = !desktop.matches;
        control.tabIndex = collapsible ? 0 : -1;
        if (collapsible) control.setAttribute('aria-expanded', block.classList.contains('is-open') ? 'true' : 'false');
        else control.removeAttribute('aria-expanded');
      };
      const toggle = () => {
        if (desktop.matches) return;
        block.classList.toggle('is-open');
        sync();
      };
      heading.addEventListener('click', toggle);
      if (control === heading) {
        heading.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        });
      }
      desktop.addEventListener('change', sync);
      sync();
    });
  };

  const init = () => {
    initHoverMenus();
    initFooter();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Move the size-guide trigger into the matching option legend (measured: right-aligned in the "Size —" row). */
(() => {
  const place = () => {
    document.querySelectorAll('[data-yv-size-trigger]').forEach((opener) => {
      const want = (opener.dataset.option || 'size').toLowerCase();
      const info = opener.closest('product-info') || document;
      const fieldset = Array.from(info.querySelectorAll('fieldset[data-option-name]')).find(
        (f) => (f.dataset.optionName || '').toLowerCase() === want
      );
      const legend = fieldset && fieldset.querySelector('legend.form__label');
      if (legend && !legend.contains(opener)) legend.appendChild(opener);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', place);
  else place();
  document.addEventListener('shopify:section:load', place);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(place));
  }
  new MutationObserver(() => place()).observe(document.body, { childList: true, subtree: true });
})();
